/**
 * Route-level error boundary — one rendering crash in a page component
 * should not nuke the whole app (login session, other tabs, FCM).
 * Wraps every <Route> element in App.tsx.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import i18n from "@/lib/i18n";

type Props = {
  children: ReactNode;
  /** Optional already-translated page name to show in the fallback — helps
   *  the user understand WHICH page failed when they hit refresh. */
  label?: string;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
     
    console.error("[ErrorBoundary]", this.props.label, error, info);
  }

  private reload = () => {
    window.location.reload();
  };

  private goHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const { label } = this.props;
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 text-start">
        <div className="max-w-md w-full rounded-lg border border-red-200 bg-red-50 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-red-900 mb-2">
            {i18n.t("common.error.title", { page: label ?? i18n.t("common.error.defaultPage") })}
          </h2>
          <p className="text-sm text-red-800 mb-4">{i18n.t("common.error.body")}</p>
          {this.state.error && (
            <pre className="text-xs bg-white rounded p-2 overflow-auto max-h-32 mb-4 text-slate-700 text-left" dir="ltr">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-2 flex-row-reverse">
            <button
              type="button"
              onClick={this.reload}
              className="px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
            >
              {i18n.t("common.refresh")}
            </button>
            <button
              type="button"
              onClick={this.goHome}
              className="px-3 py-1.5 rounded-md bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 text-sm font-medium"
            >
              {i18n.t("common.error.backToDashboard")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
